import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Container,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { ISearchGroups, ISearchResultItem } from '../Interfaces/ISearch';
import PageHeader from '../core/PageHeader';
import { useGlobalSearch } from '../hooks/search';
import { primaryButtonSx } from '../core/pvtaStyles';

type SearchGroupKey = keyof ISearchGroups;

const searchGroupLabels: Record<SearchGroupKey, string> = {
    events: 'Veranstaltungen',
    reservations: 'Reservierungen',
    storage: 'Datenablage',
    blog: 'Blog',
    accounts: 'Bundesbrüder',
};

const searchGroupOrder: SearchGroupKey[] = ['events', 'reservations', 'storage', 'blog', 'accounts'];

const SearchResultCard: React.FC<{
    item: ISearchResultItem;
    onOpen: (targetUrl: string) => void;
}> = ({ item, onOpen }) => (
    <Card
        sx={{
            cursor: item.target_url ? 'pointer' : 'default',
        }}
        onClick={() => onOpen(item.target_url)}
    >
        <CardContent>
            <Stack spacing={0.75}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {item.title}
                </Typography>
                {item.subtitle && (
                    <Typography variant="body2" color="text.secondary">
                        {item.subtitle}
                    </Typography>
                )}
                {item.meta && (
                    <Typography variant="caption" color="text.secondary">
                        {item.meta}
                    </Typography>
                )}
            </Stack>
        </CardContent>
    </Card>
);

const SearchPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialQuery = searchParams.get('q') ?? '';
    const [inputValue, setInputValue] = useState(initialQuery);
    const query = initialQuery.trim();
    const { data, isLoading } = useGlobalSearch(query, 8);

    useEffect(() => {
        setInputValue(initialQuery);
    }, [initialQuery]);

    const visibleGroups = useMemo(
        () =>
            searchGroupOrder.filter((groupKey) => data.groups[groupKey].length > 0),
        [data.groups],
    );

    const totalResults = useMemo(
        () => visibleGroups.reduce((sum, groupKey) => sum + data.groups[groupKey].length, 0),
        [data.groups, visibleGroups],
    );

    const submitSearch = () => {
        const nextQuery = inputValue.trim();
        if (!nextQuery) {
            setSearchParams({});
            return;
        }
        setSearchParams({ q: nextQuery });
    };

    const openTarget = (targetUrl: string) => {
        if (targetUrl) {
            navigate(targetUrl);
        }
    };

    return (
        <Box sx={{ py: { xs: 3, md: 4 }, width: '100%', maxWidth: '100%' }}>
            <Container maxWidth="xl">
                <PageHeader title="Suche" subtitle="Finde Veranstaltungen, Reservierungen, Dokumente, Blog und Bundesbrüder." />

                <Stack spacing={2.5}>
                    <Card>
                        <CardContent>
                            <Stack spacing={1.5}>
                                <TextField
                                    label="Projektweit suchen"
                                    placeholder="z. B. Sommerfest, Max Mustermann oder Hüttenbuch"
                                    value={inputValue}
                                    onChange={(event) => setInputValue(event.target.value)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            submitSearch();
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                                    <Button onClick={submitSearch} sx={{ ...primaryButtonSx }}>
                                        Suchen
                                    </Button>
                                    <Chip
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        label="Enter sucht projektweit"
                                    />
                                    {query && (
                                        <Chip
                                            size="small"
                                            label={`${totalResults} Treffer`}
                                        />
                                    )}
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>

                    {query.length > 0 && query.length < 2 && (
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary">
                                    Bitte gib mindestens 2 Zeichen ein.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}

                    {isLoading && query.length >= 2 && (
                        <Typography color="text.secondary">Suche läuft…</Typography>
                    )}

                    {!isLoading && query.length >= 2 && totalResults === 0 && (
                        <Card>
                            <CardContent>
                                <Typography color="text.secondary">
                                    Keine Treffer für &quot;{query}&quot; gefunden.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}

                    <Stack spacing={2.5}>
                        {visibleGroups.map((groupKey) => (
                            <Card key={groupKey}>
                                <CardContent>
                                    <Stack spacing={1.5}>
                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                {searchGroupLabels[groupKey]}
                                            </Typography>
                                            <Chip size="small" label={data.groups[groupKey].length} />
                                        </Stack>
                                        <Stack spacing={1.25}>
                                            {data.groups[groupKey].map((item) => (
                                                <SearchResultCard
                                                    key={`${groupKey}-${item.id}`}
                                                    item={item}
                                                    onOpen={openTarget}
                                                />
                                            ))}
                                        </Stack>
                                    </Stack>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                </Stack>
            </Container>
        </Box>
    );
};

export default SearchPage;
